import React, { useRef, useState, useMemo, useCallback } from "react";
import Vehicle from "@components/operatorLobby/Vehicle";
import WorldMap from "@components/operatorLobby/WorldMap";
import useWebSocket from "@components/operatorLobby/useWebSocket";
import useJoystickLeft from "@components/operatorLobby/useJoystickLeft";
import useJoystickRight from "@components/operatorLobby/useJoystickRight";
import useKeyboard from "@components/operatorLobby/useKeyboardDifferential";
import ActiveConnection from "@components/operatorLobby/ActiveConnection";
import VoiceCall from "./VoiceCall/VoiceCall";
import produce from "immer";
import { applyPatch } from "fast-json-patch";
import { flushSync } from "react-dom";
import { setApiKey } from "../../fetchApi";

export default function OperatorLobby({ token, listOfAvailableVehicles, operator, isMobile }) {
  const activeConnectionRef = useRef();
  const joystickLeft = useJoystickLeft();
  const joystickRight = useJoystickRight();
  const keyboard = useKeyboard();

  // Needed to avoid create new list on each render
  const controlUpdateSetters = useMemo(
    () => [keyboard.setControlUpdate, joystickLeft.setControlUpdate, joystickRight.setControlUpdate],
    [keyboard.setControlUpdate, joystickLeft.setControlUpdate, joystickRight.setControlUpdate]
  );
  const controlSendSetters = useMemo(
    () => [keyboard.setControlSend, joystickLeft.setControlSend, joystickRight.setControlSend],
    [keyboard.setControlSend, joystickLeft.setControlSend, joystickRight.setControlSend]
  );
  const vehicleStateRequestSetters = useMemo(
    () => [keyboard.setVehicleStateRequest, joystickLeft.setVehicleStateRequest, joystickRight.setVehicleStateRequest],
    [keyboard.setVehicleStateRequest, joystickLeft.setVehicleStateRequest, joystickRight.setVehicleStateRequest]
  );

  const infrastructure = useWebSocket({
    token,
    onMessage: onInfrastructureMessage,
    onOpen: onInfrastructureOpen,
  });
  const connectedVehicleId = operator.assignedVehicle;

  const [isStandingByDesiered, setStandingByDesire] = useState(false);
  const [voiceCallState, _setVoiceCallState] = useState(null);

  const setVoiceCallState = useCallback((arg) => {
    flushSync(() => {
      _setVoiceCallState(arg);
    });
  }, []);

  function interruptStreams() {
    infrastructure.send("infrastructure", "operatorStopDrivingRequest", null);
    setStandingByDesire(false);
  }

  const connectedVehicle = listOfAvailableVehicles.find((vehicle) => {
    return vehicle.id === connectedVehicleId;
  });

  const activeConnectionProps = {
    vehicle: connectedVehicle,
    interruptStreams,
    controlUpdateSetters,
    controlSendSetters,
    vehicleStateRequestSetters,
    sendToInfrastructure: infrastructure.send,
    isMobile,
    voiceCallState,
    listOfAvailableVehicles,
  };
  const activeConnectionComponent = (
    <ActiveConnection {...activeConnectionProps} ref={activeConnectionRef} />
  );

  function onInfrastructureMessage(message) {
    const activeConnection = activeConnectionRef.current;

    if (message.type === "initCallState") {
      setVoiceCallState(message.content);
    } else if (message.type === "diffCallState") {
      setVoiceCallState((prevState) =>
        produce(prevState, (draft) => {
          applyPatch(draft, message.content);
        })
      );
    } else if (message.type === "apiKey") {
      setApiKey(message.content);
    } else if (activeConnection) {
      activeConnection.onInfrastructureMessage(message);
    } else {
      console.error("No active connection to handle message:", message);
    }
  }

  function onInfrastructureOpen() {
    if (!isMobile) {
      infrastructure.send("infrastructure", "operatorCanVoiceCall", null);
    }
  }

  function onDeviceSelectChange(event) {
    const isKeyboardSelected = event.target.checked;
    joystickLeft.setEnable(!isKeyboardSelected);
    joystickRight.setEnable(!isKeyboardSelected);
    keyboard.setEnable(isKeyboardSelected);
  }

  const keyboardHelp = (
    <div className="row">
      <div className="col">
        <div className="p-3 border bg-light container-fluid">
          <div className="row">{keyboard.help()}</div>
        </div>
      </div>
    </div>
  );

  function onStandingByReadyClick() {
    infrastructure.send("infrastructure", "operatorStandingByRequest", null);
    setStandingByDesire(true);
  }

  function onStopStandingByReadyClick() {
    infrastructure.send("infrastructure", "operatorStopStandingByRequest", null);
    setStandingByDesire(false);
  }

  const isActuallyStandingBy = operator.state === "standingBy";
  let standingByButton = null;
  if (!isActuallyStandingBy) {
    standingByButton = (
      <button
        className="col btn btn-outline-primary"
        disabled={isActuallyStandingBy !== isStandingByDesiered}
        onClick={onStandingByReadyClick}
      >
        Ready
      </button>
    );
  } else {
    standingByButton = (
      <button
        className="col btn btn-outline-primary"
        disabled={isActuallyStandingBy !== isStandingByDesiered}
        onClick={onStopStandingByReadyClick}
      >
        Un-Ready
      </button>
    );
  }

  function onRestingClick() {
    infrastructure.send("infrastructure", "operatorRestingRequest", null);
    setStandingByDesire(false);
  }

  function onStopRestingClick() {
    infrastructure.send("infrastructure", "operatorStopRestingRequest", null);
  }

  const allowedToRest = connectedVehicle
    ? !(
        connectedVehicle.state === "parking" ||
        connectedVehicle.state === "maintenance" ||
        connectedVehicle.state === "docking"
      )
    : false;
  const restingButton = (
    <div className="parkBreakMessage">
      <button
        className="col btn btn-outline-primary"
        disabled={allowedToRest}
        onClick={onRestingClick}
      >
        Rest
      </button>
      {allowedToRest && <p> Park the vehicle to take a break</p>}
    </div>
  );
  const stopRestingButton = (
    <button
      className="col btn btn-outline-primary"
      disabled={allowedToRest}
      onClick={onStopRestingClick}
    >
      Stop resting
    </button>
  );

  const worldMap = (
    <>
      <div className="row">
        <WorldMap vehicles={listOfAvailableVehicles} />
      </div>
    </>
  );

  const vehicleSelectionComponent = (
    <div className="row">
      <h4 className="text-center">Manual takeover</h4>
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">State</th>
            <th scope="col">Battery</th>
            <th scope="col">Mission Plan</th>
            <th scope="col">Emergency</th>
            <th scope="col">Assignation</th>
          </tr>
        </thead>
        <tbody>
          {listOfAvailableVehicles.map((vehicle) => (
            <tr key={vehicle.id}>
              <Vehicle
                key={vehicle.id}
                token={token}
                vehicle={vehicle}
                sendToInfrastructure={infrastructure.send}
              />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const deviceSelection = (
    <fieldset className="border border-secondary rounded text-secondary p-2">
      <legend className="w-auto">Settings</legend>
      <div className="form-check form-switch">
        <input
          className="form-check-input"
          type="checkbox"
          id="devselect"
          onChange={onDeviceSelectChange}
        />
        <label className="form-check-label" htmlFor="isTeleoperatorAvailable">
          Enable dev mode and use the keyboard instead of driving device
        </label>
      </div>
    </fieldset>
  );

  const isOperatorResting = operator.state === "resting";

  let content = (
    <>
      <VoiceCall
        infrastructure={infrastructure}
        voiceCallState={voiceCallState}
        isMobile={isMobile}
      />
      <h2>Operator Lobby</h2>
      <br />
      <h3 className="text-center">Operator State: {operator.state}</h3>
      <div className="row">
        <div className="col text-center p-2">
          <h4>Take a break</h4>
          {isOperatorResting ? stopRestingButton : restingButton}
        </div>
        {!isOperatorResting && !connectedVehicle && (
          <>
            <div className="col text-center p-2">
              <h4>Automatic vehicle assignment</h4>
              {standingByButton}
            </div>
          </>
        )}
      </div>
      {!isOperatorResting && (
        <>
          <br />
          {!isMobile && (
            <>
              {deviceSelection}
              {keyboard.enable && keyboardHelp}
            </>
          )}
          <br />
          {!connectedVehicle && worldMap}
          <br />
          {connectedVehicle ? activeConnectionComponent : vehicleSelectionComponent}
        </>
      )}
    </>
  );

  return infrastructure.isConnected ? content : <h2>Connecting webSocket to the backend...</h2>;
}
